import React, { useEffect, useState, useRef } from 'react'; // eslint-disable-line no-unused-vars
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../msalConfig';
import { useUser } from '../../contexts/UserContext';
import { logAccess } from '../../utils/userLog';
import Logo from '../pages/matrixBoard/brand/Logo';

/**
 * 인증이 필요한 라우트를 보호하는 컴포넌트
 * 로그인되지 않은 사용자에게 로그인 페이지를 표시
 */
// Favicon 복원 함수 (MSAL 리다이렉트 후 Microsoft favicon이 남는 문제 해결)
const FAVICON_SVG = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' rx='22' fill='%232563eb'/><text x='50' y='72' font-size='62' font-weight='900' text-anchor='middle' fill='white' font-family='system-ui'>f</text></svg>";

const restoreFavicon = () => {
  // 기존 favicon 링크 찾기
  let link = document.querySelector("link[rel~='icon']");

  if (link) {
    link.href = FAVICON_SVG;
  } else {
    // favicon 링크가 없으면 새로 생성
    link = document.createElement('link');
    link.rel = 'icon';
    link.href = FAVICON_SVG;
    document.head.appendChild(link);
  }
};

const ProtectedRoute = ({ children }) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const { fetchUserInfo, user } = useUser();
  const loggedRef = useRef(false);
  // 게스트 모드(SSO 미사용 단독 배포) — 이름만 입력받아 협업. localStorage 에 저장.
  const AUTH_MODE = process.env.REACT_APP_AUTH_MODE;
  const [guestName, setGuestName] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('guestName')) || '');
  const [guestInput, setGuestInput] = useState('');

  // 인증 완료 + 사용자 정보 로드 완료 시 접근 로그 기록 (1회만)
  useEffect(() => {
    if (isAuthenticated && user && !loggedRef.current) {
      loggedRef.current = true;
      const pathname = window.location.pathname;
      // matrix-board는 Files.js에서 activeBrdCd와 함께 로그 기록
      if (pathname.includes('/matrix-board')) return;
      const pageBrdMatch = pathname.match(/\/interstellar\/([^/]+)\//);
      const page_brd_cd = pageBrdMatch ? pageBrdMatch[1] : null;
      logAccess({
        user: `${user.userId}(${user.name})`,
        email: user.email,
        brd_cd: user.brdCd || null,
        page_brd_cd,
        page_nm: pathname,
      });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // 인증 진행 중이 아니면 로딩 종료
    if (inProgress === InteractionStatus.None) {
      setIsLoading(false);
    }
  }, [inProgress]);

  // 게스트 모드 — 이름이 정해지면 게스트 유저 정보 로드(서버가 X-Guest-Name 로 게스트 신원 반환).
  useEffect(() => {
    if (AUTH_MODE === 'guest' && guestName && !user) fetchUserInfo();
  }, [AUTH_MODE, guestName, user, fetchUserInfo]);

  // 인증 완료 후 favicon 복원 및 사용자 정보 로드
  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None && accounts.length > 0) {

      // 약간의 딜레이 후 favicon 복원 (DOM 로딩 완료 보장)
      setTimeout(() => {
        restoreFavicon();
      }, 100);

      // Access Token 가져오기
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      }).then((response) => {
        localStorage.setItem('authToken', response.idToken);

        // 사용자 정보가 아직 로드되지 않았다면 로드
        if (!user) {
          fetchUserInfo();
        }
      }).catch((error) => {
        console.error('ProtectedRoute: Access Token 획득 실패:', error);

        // InteractionRequiredAuthError인 경우 리디렉트로 재시도
        if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required' || error.errorCode === 'login_required') {
          
          instance.acquireTokenRedirect({
            ...loginRequest,
            account: accounts[0]
          }).catch((redirectError) => {
            console.error('ProtectedRoute: 리디렉트 토큰 획득 실패:', redirectError);
          });
        }
      });
    }
  }, [isAuthenticated, inProgress, accounts, user, fetchUserInfo, instance]);

  // ── 게스트 모드(SSO 미사용) — 이름만 입력받아 시작. 이후 실시간 협업은 동일하게 동작 ──
  if (AUTH_MODE === 'guest') {
    if (guestName) return <>{children}</>;
    const startGuest = () => {
      const n = guestInput.trim();
      if (!n) return;
      localStorage.setItem('guestName', n);
      setGuestName(n);
    };
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw', backgroundColor: '#e5e7eb', flexDirection: 'column', gap: 20, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <Logo height={56} />
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>이름을 입력하고 시작하세요</p>
        <input
          value={guestInput}
          onChange={(e) => setGuestInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') startGuest(); }}
          placeholder="이름 (예: 이경선)"
          autoFocus
          style={{ padding: '10px 14px', fontSize: 15, border: '1px solid #d1d5db', borderRadius: 8, width: 240, outline: 'none' }}
        />
        <button
          onClick={startGuest}
          disabled={!guestInput.trim()}
          style={{ padding: '10px 48px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: guestInput.trim() ? 'pointer' : 'not-allowed', opacity: guestInput.trim() ? 1 : 0.6, fontSize: 15, fontWeight: 600 }}
        >
          시작
        </button>
      </div>
    );
  }

  // 로딩 중일 때 표시할 화면
  if (isLoading || inProgress !== InteractionStatus.None) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: '#e5e7eb',
          flexDirection: 'column',
          gap: '24px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* 로고 */}
        <Logo height={52} style={{ marginBottom: 12 }} />

        {/* 로딩 스피너 */}
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />

        {/* 로딩 메시지 */}
        <p
          style={{
            fontSize: '16px',
            color: '#6b7280',
            textAlign: 'center',
            margin: 0,
            fontWeight: 500
          }}
        >
          Microsoft 로그인 진행 중...
        </p>

        {/* 애니메이션 정의 */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // 인증되지 않았으면 로그인 안내 화면
  if (!isAuthenticated) {
    const handleLogin = async () => {
      // 이미 인증 진행 중이면 무시
      if (inProgress !== InteractionStatus.None) {
        
        return;
      }

      try {
        // 리디렉트 방식 사용 (팝업 차단 방지)
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        console.error("로그인 실패:", error);
      }
    };

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: '#e5e7eb',
          flexDirection: 'column',
          gap: '24px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* flow 로고 */}
        <Logo height={56} />

        {/* 서브타이틀 */}
        <p
          style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: 0,
            marginBottom: '4px'
          }}
        >
          워크플로우·이미지·데이터를 한 캔버스에서
        </p>

        {/* SSO 로그인 버튼 */}
        <button
          onClick={handleLogin}
          disabled={inProgress !== InteractionStatus.None}
          style={{
            marginTop: '4px',
            padding: '12px 72px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: inProgress !== InteractionStatus.None ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            fontWeight: 600,
            opacity: inProgress !== InteractionStatus.None ? 0.7 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (inProgress === InteractionStatus.None) {
              e.currentTarget.style.backgroundColor = '#1d4ed8';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2563eb';
          }}
        >
          {inProgress !== InteractionStatus.None ? '로그인 진행 중...' : 'SSO 로그인'}
        </button>

        {/* 문의 안내 */}
        <p
          style={{
            fontSize: '13px',
            color: '#9ca3af',
            textAlign: 'center',
            margin: 0,
            marginTop: '8px'
          }}
        >
          로그인 문제 발생 시 AI엔지니어링팀 이경선 담당에게 문의 바랍니다.
        </p>
      </div>
    );
  }

  // 인증되었으면 자식 컴포넌트 렌더링
  return <>{children}</>;
};

export default ProtectedRoute;
